import type { Language, Theme, User } from '@metadevprofile/shared';
import { startImpersonationSession } from '@/admin-user/stores/impersonation-slice';
import { setUser } from '@/auth/stores/auth-slice';
import { env } from '@/common/constants/env';
import type { AppDispatch } from '@/common/redux/store';
import { acceptCookies, type ConsentStatus, rejectCookies } from '@/cookie-consent/stores/cookie-consent-slice';
import { setPreferences } from '@/profile/stores/preferences-slice';

interface ZustandPersistEnvelope<T> {
  state?: T;
}

const readLegacyState = <T>(storageArea: Storage, key: string): T | null => {
  try {
    const raw = storageArea.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ZustandPersistEnvelope<T>;
    return parsed.state ?? null;
  } catch {
    return null;
  }
};

/**
 * One-shot bridge from the pre-Redux Zustand stores to the new slices. Must run synchronously
 * right after `configureStore()` and before `persistStore()` resolves its (async) rehydration:
 * the legacy key names never match the new redux-persist keys, so REHYDRATE finds nothing there
 * and only marks `_persist.rehydrated = true` without touching state we've already dispatched
 * into — see store.ts. Deletes the legacy keys once read, so this is a no-op on every later boot.
 */
export const migrateLegacyStores = (dispatch: AppDispatch): void => {
  const legacyAuth = readLegacyState<{ user: User | null }>(localStorage, 'auth-storage');
  if (legacyAuth?.user) {
    dispatch(setUser(legacyAuth.user));
  }
  localStorage.removeItem('auth-storage');

  const legacyPreferences = readLegacyState<{ language: Language; theme: Theme }>(
    localStorage,
    `profile-${env.APP_SLUG}`,
  );
  if (legacyPreferences) {
    dispatch(setPreferences(legacyPreferences));
  }
  localStorage.removeItem(`profile-${env.APP_SLUG}`);

  const legacyConsent = readLegacyState<{ consent: ConsentStatus }>(localStorage, 'cookie-consent-storage');
  if (legacyConsent?.consent === 'accepted') dispatch(acceptCookies());
  if (legacyConsent?.consent === 'rejected') dispatch(rejectCookies());
  localStorage.removeItem('cookie-consent-storage');

  const legacyImpersonation = readLegacyState<{
    active: boolean;
    targetUser: User | null;
    returnTo: string | null;
    adminAccessToken: string | null;
    adminRefreshToken: string | null;
  }>(sessionStorage, 'impersonation-storage');
  if (legacyImpersonation?.active && legacyImpersonation.targetUser) {
    dispatch(
      startImpersonationSession({
        targetUser: legacyImpersonation.targetUser,
        returnTo: legacyImpersonation.returnTo,
        adminAccessToken: legacyImpersonation.adminAccessToken,
        adminRefreshToken: legacyImpersonation.adminRefreshToken,
      }),
    );
  }
  sessionStorage.removeItem('impersonation-storage');
};
